import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ClipboardCopy, MoreVertical } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { darkGreenColor, semiBoldText, smallTextSize } from '../../../styling/stylingHelper';

interface OrderActionsMenuProps {
  /** Copies the line items; resolves false when the clipboard rejected the write. */
  onCopyLineItems: () => Promise<boolean>;
}

/**
 * Overflow menu beside the Orders table's Export button, holding the actions that are not the
 * Excel download. Deliberately separate from that button: Export stays exactly the one-click
 * download people already use.
 */
export const OrderActionsMenu = ({ onCopyLineItems }: OrderActionsMenuProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [justCopied, setJustCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Reset the "Copied!" confirmation shortly after it shows. The menu stays open so the user
  // actually sees it.
  useEffect(() => {
    if (!justCopied) return;
    const timer = setTimeout(() => setJustCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [justCopied]);

  const handleCopy = async () => {
    if (await onCopyLineItems()) setJustCopied(true);
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Same border, height and colour as the Export button it sits next to. */}
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t('orderHistory.export.moreActions')}
        className={cn(
          'inline-flex items-center justify-center px-2.5 py-2 rounded-xl border transition-colors shrink-0',
          'border-darkGreenColor dark:border-darkGreenColor-dark bg-white dark:bg-background-dark hover:bg-neutral-50 dark:hover:bg-backgroundDarker-dark',
          smallTextSize,
          semiBoldText
        )}
        style={{ color: darkGreenColor }}
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 min-w-[220px] rounded-xl border border-[#E5E7EB] dark:border-borderPrimary-dark bg-white dark:bg-background-dark shadow-lg py-1 z-50"
        >
          <button
            type="button"
            role="menuitem"
            onClick={handleCopy}
            className={cn(
              'w-full flex items-center gap-2 px-3 py-2 text-left text-[14px] leading-5 font-semibold',
              'text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark'
            )}
          >
            {justCopied
              ? <Check className="w-5 h-5 shrink-0 text-[#0E8965] dark:text-darkGreenColor-dark" />
              : <ClipboardCopy className="w-5 h-5 shrink-0" />}
            {justCopied ? t('orderHistory.export.copied') : t('orderHistory.export.copyLineItems')}
          </button>
        </div>
      )}
    </div>
  );
};
